import React, { useEffect, useState } from "react";
import {
	useNavigate,
	useParams,
	useSearchParams,
	useLocation,
} from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumb from "components/Common/Breadcrumb";
import {
	Form,
	FormGroup,
	Label,
	Input,
	Button,
	FormFeedback,
	Row,
	Col,
} from "reactstrap";
import { Checkbox } from "@mui/material";

import html2pdf from "html2pdf.js";

import meetingService from "services/MeetingService";
import userService from "services/UserService";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import Loading from "components/Common/Loading";
import {
	FloppyDisk,
	Trash,
	FilePdf,
	Copy,
	Square,
	CheckSquare,
} from "@phosphor-icons/react";
import moment from "moment-timezone";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import uploadFileService from "services/UploadFileService";
import CkEditor from "./CkEditor";

const customStyles = {
	control: (styles) => ({
		...styles,
		borderColor: "#ccc",
		borderRadius: 5,
		borderWidth: 1,
	}),
	option: (styles, state) => ({
		...styles,
		backgroundColor: state.isFocused ? "#f1b44c" : "#fff",
		color: state.isFocused ? "#fff" : "#000",
		cursor: "pointer",
		"&:hover": {
			backgroundColor: "#f1b44c",
			color: "#fff",
		},
	}),
	multiValue: (styles) => ({
		...styles,
		backgroundColor: "#f1b44c",
		borderRadius: 5,
		color: "#fff",
	}),
	multiValueLabel: (styles) => ({
		...styles,
		fontWeight: "bold",
		color: "#fff",
	}),
	multiValueRemove: (styles) => ({
		...styles,
		color: "#fff",
		cursor: "pointer",
	}),
};

const FormMeeting = () => {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();

	const [formData, setFormData] = useState({
		title: "",
		date_start: "",
		hour_start: "",
		date_end: "",
		hour_end: "",
		place: "",
		description: "",
		notification_type: "",
		notification_time: "",
		notification_period: "",
		participants: [],
		timezone: "",
		recurrence: false,
		recurrence_freq: "",
		recurrence_end_date: null,
		recurrence_group_id: "",
	});

	const [companyUsers, setCompanyUsers] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [dataLoading, setDataLoading] = useState(false);
	const [formErrors, setFormErrors] = useState({});
	const [isLoadingDelete, setIsLoadingDelete] = useState(false);
	const [isLoadingPDF, setIsLoadingPDF] = useState(false);
	const [checkboxKey, setCheckboxKey] = useState(0);

	// Sincroniza o checkboxKey com o formData.recurrence inicial
	useEffect(() => {
		setCheckboxKey((prev) => prev + 1);
	}, [formData.recurrence]);

	// Trazendo por padrão o usuário logado para o agendamento
	useEffect(() => {
		const addCurrentUserToParticipants = async () => {
			if (!id) {
				// Somente para novos agendamentos
				try {
					const currentUser = await userService.getUserInfo();
					if (currentUser) {
						setFormData((prev) => ({
							...prev,
							participants: [
								{
									user_id: currentUser.id,
									name: currentUser.name,
									confirmed: 0,
								},
							],
						}));
					}
				} catch (error) {
					console.error("Erro ao buscar usuário atual:", error);
				}
			}
		};

		addCurrentUserToParticipants();
	}, [id]);

	// Busca de usuários da empresa
	const fetchCompanyUsers = async () => {
		try {
			const users = await userService.getAllCompany();
			// Ordenando a lista de usuários em ordem alfabética
			users.sort((a, b) => a.name.localeCompare(b.name));
			setCompanyUsers(users);
		} catch (error) {
			console.error("Erro ao buscar usuários:", error);
		}
	};

	// Busca dados de um meeting específico (para edição)
	const fetchMeeting = async () => {
		if (!id) return;
		try {
			setDataLoading(true);
			const meetingData = await meetingService.getById(id);

			// Mapeia os participantes para incluir o nome do usuário
			const participantsWithNames =
				meetingData.participants?.map((participant) => {
					const user = companyUsers.find(
						(user) => user.id === participant.user_id,
					);
					return {
						...participant,
						name: user?.name || "",
					};
				}) || [];

			setFormData({
				...formData,
				title: meetingData.title,
				date_start: meetingData.date_start?.substring(0, 10),
				hour_start: meetingData.hour_start,
				date_end: meetingData.date_end?.substring(0, 10),
				hour_end: meetingData.hour_end,
				place: meetingData.place,
				description: meetingData.description || "",
				notification_type: meetingData.notification_type || "",
				notification_time: meetingData.notification_time || "",
				notification_period: meetingData.notification_period || "",
				recurrence: meetingData.recurrence || false,
				recurrence_freq: meetingData.recurrence_freq || "",
				recurrence_end_date:
					meetingData.recurrence_end_date?.substring(0, 10) || null,
				recurrence_group_id: meetingData.recurrence_group_id || "",
				participants: participantsWithNames,
			});
		} catch (error) {
			console.error("Erro ao buscar meeting:", error);
		} finally {
			setDataLoading(false);
		}
	};

	useEffect(() => {
		const tz = moment.tz.guess();
		setFormData((prev) => ({
			...prev,
			timezone: tz,
		}));
	}, []);

	useEffect(() => {
		fetchCompanyUsers();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (companyUsers.length > 0) {
			fetchMeeting();
		}
		// eslint-disable-next-line
	}, [companyUsers]);

	// Se veio uma data via query param "?date=2025-01-01"
	useEffect(() => {
		const dateParam = searchParams.get("date");
		if (dateParam && !id) {
			setFormData((prev) => ({
				...prev,
				date_start: dateParam,
				date_end: dateParam,
			}));
		}
		// eslint-disable-next-line
	}, [id]);

	// Efeito para carregar dados de um meeting duplicado
	useEffect(() => {
		if (location.state?.duplicatedMeeting) {
			setDataLoading(true);
			// Loading para feedback visual da duplicação (1s)
			setTimeout(() => {
				setFormData((prev) => ({
					...prev,
					...location.state.duplicatedMeeting,
				}));
				setDataLoading(false);
			}, 1000);
		}
	}, [location.state]);

	const handleInputChange = (e) => {
		const { name, value, type } = e.target;
		let val = value;

		if (name === "notification_time" && type === "number") {
			val = val.replace(/\D/g, "");
		}

		setFormData((prev) => ({
			...prev,
			[name]: val,
		}));
	};

	// Quando o CKEditor mudar, recebemos a string HTML
	const handleDescriptionChange = (event, editor) => {
		const data = editor.getData();

		// Garantir que o conteúdo seja processado corretamente
		// e converter qualquer sintaxe Markdown para HTML
		const processedData = processContentForStorage(data);

		setFormData((prev) => ({
			...prev,
			description: processedData,
		}));
	};

	// Função para processar o conteúdo antes de armazenar
	const processContentForStorage = (content) => {
		// Converter sintaxe Markdown de imagens para tags HTML
		const markdownImageRegex = /!\[(.*?)\]\((.*?)\)/g;

		// Substituir todos os padrões encontrados por tags <img>
		const processedContent = content.replace(
			markdownImageRegex,
			(match, alt, url) => {
				return `<img src="${url}" alt="${
					alt || ""
				}" style="max-width: 100%">`;
			},
		);

		return processedContent;
	};

	// Validação simples + Validação de data/hora
	const validate = () => {
		const errors = {};

		if (!formData.title) errors.title = "Campo obrigatório";
		if (!formData.date_start) errors.date_start = "Campo obrigatório";
		if (!formData.hour_start) errors.hour_start = "Campo obrigatório";
		if (!formData.date_end) errors.date_end = "Campo obrigatório";
		if (!formData.hour_end) errors.hour_end = "Campo obrigatório";
		if (!formData.place) errors.place = "Campo obrigatório";
		if (!formData.description) errors.description = "Campo obrigatório";

		// Combina a data + hora inicial e final para comparar
		if (
			formData.date_start &&
			formData.hour_start &&
			formData.date_end &&
			formData.hour_end
		) {
			const start = moment(
				`${formData.date_start} ${formData.hour_start}`,
				"YYYY-MM-DD HH:mm",
			);
			const end = moment(
				`${formData.date_end} ${formData.hour_end}`,
				"YYYY-MM-DD HH:mm",
			);

			// Se a data/hora final for igual ou anterior à inicial, gera erro.
			if (start.isSameOrAfter(end)) {
				errors.date_end =
					"A data/hora final deve ser maior que a data/hora inicial.";
			}
		}

		return errors;
	};

	const generatePDFBase = async (conteudo, options = {}) => {
		// Converter sintaxe Markdown de imagens para tags HTML
		const processMarkdownImages = (htmlContent) => {
			// Regex para encontrar padrões ![](url) ou ![texto alt](url)
			const markdownImageRegex = /!\[(.*?)\]\((.*?)\)/g;

			// Substituir todos os padrões encontrados por tags <img>
			return htmlContent.replace(
				markdownImageRegex,
				(match, alt, url) => {
					return `<img src="${url}" alt="${
						alt || ""
					}" style="max-width: 100%">`;
				},
			);
		};

		// Pré-processar o conteúdo para converter imagens Markdown
		const conteudoProcessado = processMarkdownImages(conteudo);

		// Preserva o HTML da tabela e converte quebras de linha em <br>
		const conteudoFormatado = conteudoProcessado.replace(/\n/g, "<br>"); // Converte quebras de linha em <br>

		const container = document.createElement("div");
		container.style.width = "210mm"; // largura A4 (pode ajustar)
		container.style.margin = "0 auto";
		container.classList.add("ck-content");
		container.innerHTML = conteudoFormatado;

		// Função para baixar imagem e converter para data URL (base64)
		// Isso contorna problemas de CORS usando AllOrigins
		const convertImageToDataUrl = (imgElement) => {
			return new Promise(async (resolve) => {
				// Se a imagem já for uma data URL, não é necessário converter
				if (imgElement.src.startsWith("data:")) {
					resolve();
					return;
				}

				try {
					// Usar AllOrigins - proxy confiável e popular
					const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(
						imgElement.src,
					)}`;

					const response = await fetch(proxyUrl, {
						method: "GET",
						headers: {
							Accept: "image/*",
						},
					});

					if (response.ok) {
						const blob = await response.blob();
						const reader = new FileReader();
						reader.onload = () => {
							imgElement.src = reader.result;
							resolve();
						};
						reader.onerror = () => {
							console.error("Erro ao converter blob para base64");
							resolve(); // Continuar mesmo com erro
						};
						reader.readAsDataURL(blob);
					} else {
						console.warn(
							`AllOrigins retornou HTTP ${response.status} para: ${imgElement.src}`,
						);
						resolve(); // Continuar com imagem original
					}
				} catch (error) {
					console.warn("Erro ao usar AllOrigins:", error.message);
					resolve(); // Continuar com imagem original
				}
			});
		};

		// Função para garantir que as imagens sejam carregadas antes de gerar o PDF
		const preloadImages = async () => {
			const imgElements = container.querySelectorAll("img");
			if (imgElements.length === 0) return Promise.resolve();

			// Primeiro tentamos converter todas as imagens para data URLs
			const conversionPromises = Array.from(imgElements).map((img) =>
				convertImageToDataUrl(img),
			);
			await Promise.all(conversionPromises);

			// Depois aguardamos o carregamento de todas as imagens (agora como data URLs)
			const imagePromises = Array.from(imgElements).map((img) => {
				return new Promise((resolve) => {
					if (img.complete) {
						resolve();
					} else {
						img.onload = () => {
							resolve();
						};
						img.onerror = () => {
							console.error(`Erro ao carregar imagem`);
							resolve(); // Resolva mesmo com erro para não bloquear todo o processo
						};
					}
				});
			});

			return Promise.all(imagePromises);
		};

		// Aguarda todas as imagens carregarem
		await preloadImages();

		const estilos = `
		  .main-container {
			font-family: "Lato";
			width: 100%;
			margin-left: auto;
			margin-right: auto;
		  }
		  .ck-content {
			font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
			  Helvetica, Arial, sans-serif;
			line-height: 1.6;
			color: #333;
			padding: 1rem;
			min-height: 200px;
		  }
		  .ck-content p { margin: 0 0 1em; }
		  .ck-content ul,
		  .ck-content ol { margin: 1em 0; padding-left: 2em; }
		  .ck-content li { margin: 0.5em 0; }
		  .ck-content table {
			border-collapse: collapse;
			margin: 1em 0;
			width: 100%;
		  }
		  .ck-content table td,
		  .ck-content table th {
			border: 1px solid #ddd;
			padding: 0.5em;
			vertical-align: middle;
		  }
		  .ck-content table th {
			background-color: #f5f5f5;
			font-weight: 600;
		  }
		  .ck-content img {
			max-width: 100%;
			height: auto;
			margin: 1em 0;
		  }
		  .ck-content a {
			color: #0066cc;
			text-decoration: none;
		  }
		  .ck-content a:hover {
			text-decoration: underline;
		  }
		  .ck-content blockquote {
			margin: 1em 0;
			padding: 0.5em 1em;
			border-left: 4px solid #ddd;
			background-color: #f9f9f9;
			font-style: italic;
		  }
		  .ck-content code {
			font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
			background-color: #f5f5f5;
			padding: 0.2em 0.4em;
			border-radius: 3px;
			font-size: 0.9em;
		  }
		  .ck-content h1,
		  .ck-content h2,
		  .ck-content h3,
		  .ck-content h4,
		  .ck-content h5,
		  .ck-content h6 {
			margin: 1.5em 0 0.5em;
			font-weight: 600;
			line-height: 1.25;
		  }
		  .ck-content h1 { font-size: 2em; }
		  .ck-content h2 { font-size: 1.5em; }
		  .ck-content h3 { font-size: 1.25em; }
		  .ck-content h4 { font-size: 1.1em; }
		  .ck-content h5 { font-size: 1em; }
		  .ck-content h6 { font-size: 0.9em; }
		  .ck-content h3.category {
			font-family: "Oswald";
			font-size: 20px;
			font-weight: bold;
			color: #555;
			letter-spacing: 10px;
			margin: 0;
			padding: 0;
		  }
		  .ck-content h2.document-title {
			font-family: "Oswald";
			font-size: 50px;
			font-weight: bold;
			margin: 0;
			padding: 0;
			border: 0;
		  }
		  .ck-content h3.document-subtitle {
			font-family: "Oswald";
			font-size: 20px;
			color: #555;
			margin: 0 0 1em;
			font-weight: bold;
			padding: 0;
		  }
		  .ck-content p.info-box {
			--background-size: 30px;
			--background-color: #e91e63;
			padding: 1.2em 2em;
			border: 1px solid var(--background-color);
			background: linear-gradient(
				135deg,
				var(--background-color) 0%,
				var(--background-color) var(--background-size),
				transparent var(--background-size)
			  ),
			  linear-gradient(
				135deg,
				transparent calc(100% - var(--background-size)),
				var(--background-color) calc(100% - var(--background-size)),
				var(--background-color)
			  );
			border-radius: 10px;
			margin: 1.5em 2em;
			box-shadow: 5px 5px 0 #ffe6ef;
		  }
		  .ck-content span.marker { background: yellow; }
		  .ck-content span.spoiler { background: #000; color: #000; }
		  .ck-content span.spoiler:hover { background: #000; color: #fff; }
		  .ck-content .button {
			display: inline-block;
			width: 260px;
			border-radius: 8px;
			margin: 0 auto;
			padding: 12px;
			color: #ffffff;
			font-size: 24px;
			font-weight: 700;
			text-align: center;
			text-decoration: none;
		  }
		  .ck-content .button--green { background-color: #406b1e; }
		  .ck-content .button--black { background-color: #141517; }
		  .ck-content strong { font-weight: 600; }
		  .ck-content em { font-style: italic; }
		  .ck-content pre {
			background-color: #f5f5f5;
			padding: 1em;
			border-radius: 4px;
			overflow-x: auto;
			margin: 1em 0;
		  }
		  .ck-content pre code {
			background-color: transparent;
			padding: 0;
		  }
		  .ck-content ul ul,
		  .ck-content ol ol,
		  .ck-content ul ol,
		  .ck-content ol ul {
			margin: 0.5em 0;
		  }
		  @media print {
			body { margin: 0 !important; }
		  }
		`;

		const style = document.createElement("style");
		style.innerHTML = estilos;
		container.prepend(style);

		document.body.appendChild(container);

		const defaultOptions = {
			margin: 0,
			filename: "agenda.pdf",
			image: { type: "jpeg", quality: 0.98 },
			html2canvas: {
				scale: 1.5,
				useCORS: false,
				allowTaint: true,
				foreignObjectRendering: false,
				logging: false,
				backgroundColor: "#ffffff",
				width: container.scrollWidth,
				height: container.scrollHeight,
				scrollX: 0,
				scrollY: 0,
				windowWidth: container.scrollWidth,
				windowHeight: container.scrollHeight,
				onclone: (clonedDoc) => {
					const images = clonedDoc.querySelectorAll("img");
					images.forEach((img) => {
						if (img.width > 600) {
							img.style.width = "600px";
							img.style.height = "auto";
						}
					});

					const clonedElement =
						clonedDoc.querySelector(".ck-content");
					if (clonedElement) {
						clonedElement.style.width = "210mm";
						clonedElement.style.padding = "20mm";
						clonedElement.style.boxSizing = "border-box";
						clonedElement.style.backgroundColor = "#ffffff";
						clonedElement.style.overflow = "visible";
					}
				},
			},
			jsPDF: {
				unit: "mm",
				format: "a4",
				orientation: "portrait",
				compress: true,
				hotfixes: ["px_scaling"],
			},
			pagebreak: {
				mode: ["avoid-all", "css", "legacy"],
				before: ".page-break-before",
				after: ".page-break-after",
				avoid: [
					"h1",
					"h2",
					"h3",
					"h4",
					"h5",
					"h6",
					"table",
					"img",
					"pre",
					"blockquote",
				],
			},
		};

		// Adiciona um wrapper para garantir que todo o conteúdo seja capturado
		const wrapper = document.createElement("div");
		wrapper.style.width = "100%";
		wrapper.style.height = "auto";
		wrapper.style.overflow = "visible";
		wrapper.style.position = "relative";
		wrapper.appendChild(container);

		document.body.appendChild(wrapper);

		return {
			wrapper,
			options: { ...defaultOptions, ...options },
			cleanup: () => {
				if (wrapper.parentNode) {
					document.body.removeChild(wrapper);
				}
			},
		};
	};

	const handleExportPDF = async () => {
		setIsLoadingPDF(true);
		try {
			const { wrapper, options, cleanup } = await generatePDFBase(
				formData.description,
			);

			await html2pdf().set(options).from(wrapper).save();
			cleanup();
		} catch (error) {
			console.error("Erro ao gerar PDF:", error);
			await SendaCustomSwal.fire({
				title: "Erro",
				text: "Ocorreu um erro ao gerar o PDF. Verifique se há problemas com as imagens.",
				iconHtml: '<i class="error-icon"></i>',
				confirmButtonText: "Ok",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsLoadingPDF(false);
		}
	};

	const generatePDFForUpload = async () => {
		const { wrapper, options, cleanup } = await generatePDFBase(
			formData.description,
		);

		try {
			// Gera o PDF e retorna como blob
			const pdfBlob = await html2pdf()
				.set(options)
				.from(wrapper)
				.output("blob");

			// Cria um FormData para upload
			const formData = new FormData();
			formData.append(
				"file",
				pdfBlob,
				`${formData.title || "documento"}.pdf`,
			);

			// Faz o upload do arquivo
			const response = await uploadFileService.create(formData);

			// Remove o container temporário
			cleanup();

			// Retorna o link do arquivo
			return response.link;
		} catch (error) {
			cleanup(); // Garante que o wrapper seja removido mesmo em caso de erro
			console.error("Erro ao gerar ou fazer upload do PDF:", error);
			throw error;
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errors = validate();
		setFormErrors(errors);
		if (Object.keys(errors).length > 0) return;

		setIsLoading(true);
		try {
			// Se for edição e o evento for recorrente e tiver um recurrence_group_id
			let editScope;
			if (id && formData.recurrence && formData.recurrence_group_id) {
				const result = await SendaCustomSwal.fire({
					title: "Este evento faz parte de uma série recorrente",
					text: "O que você deseja fazer?",
					html: `
						<p>Você tem duas opções:</p>
						<p><strong>Editar apenas este:</strong> Este evento se tornará único (a opção de recorrência será desmarcada). Os próximos eventos continuarão formando uma nova série de recorrência.</p>
						<p><strong>Editar este e todos os futuros:</strong> Esta alteração afetará este evento e todos os eventos futuros da série.</p>
					`,
					icon: "question",
					showCancelButton: true,
					showDenyButton: true,
					confirmButtonText: "Editar apenas este",
					denyButtonText: "Editar este e todos os futuros",
					cancelButtonText: "Cancelar",
					customClass: {
						confirmButton: "custom-single-button",
						denyButton: "custom-future-button",
						cancelButton: "custom-cancel-button",
					},
				});

				if (result.isDismissed) {
					setIsLoading(false);
					return;
				}

				editScope = result.isDenied ? "future" : "single";
			}

			// Gera o PDF e faz upload
			const pdfLink = await generatePDFForUpload();

			const { participants = [] } = formData;
			const filteredParticipants = participants.filter(
				(participant) => participant.user_id?.trim() !== "",
			);

			const finalData = {
				...formData,
				participants: filteredParticipants,
				link_pdf_desc: pdfLink,
				...(editScope && { edit_scope: editScope }),
			};

			if (id) {
				await meetingService.put(id, finalData);
				await SendaCustomSwal.fire({
					title: "Sucesso!",
					text: "Agendamento editado com sucesso.",
					iconHtml: '<i class="success-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} else {
				// Criação
				await meetingService.post(finalData);
				await SendaCustomSwal.fire({
					title: "Sucesso!",
					text: "Agendamento criado com sucesso.",
					iconHtml: '<i class="success-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
			navigate("/agenda");
		} catch (error) {
			console.error("Erro ao salvar agendamento:", error);
			await SendaCustomSwal.fire({
				title: "Erro",
				text: "Ocorreu um erro ao salvar a reunião.",
				iconHtml: '<i class="error-icon"></i>',
				confirmButtonText: "Ok",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Loading isLoading={dataLoading}>
			<div className="page-content">
				<Breadcrumb
					title="Agenda"
					breadcrumbItem={id ? "Editar Meeting" : "Novo Meeting"}
					backLink="/agenda"
				/>
				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<Form onSubmit={handleSubmit}>
						<Row>
							<Col md={6}>
								<FormGroup>
									<Label for="title">
										Título{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="title"
										id="title"
										value={formData.title}
										onChange={handleInputChange}
										invalid={!!formErrors.title}
									/>
									{formErrors.title && (
										<FormFeedback>
											{formErrors.title}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={3}>
								<FormGroup>
									<Label for="date_start">
										Data Inicial{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="date"
										name="date_start"
										id="date_start"
										value={formData.date_start}
										onChange={handleInputChange}
										invalid={!!formErrors.date_start}
									/>
									{formErrors.date_start && (
										<FormFeedback>
											{formErrors.date_start}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={3}>
								<FormGroup>
									<Label for="hour_start">
										Hora Inicial{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="time"
										name="hour_start"
										id="hour_start"
										value={formData.hour_start}
										onChange={handleInputChange}
										invalid={!!formErrors.hour_start}
									/>
									{formErrors.hour_start && (
										<FormFeedback>
											{formErrors.hour_start}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md={3}>
								<FormGroup>
									<Label for="date_end">
										Data Final{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="date"
										name="date_end"
										id="date_end"
										value={formData.date_end}
										onChange={handleInputChange}
										invalid={!!formErrors.date_end}
									/>
									{formErrors.date_end && (
										<FormFeedback>
											{formErrors.date_end}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={3}>
								<FormGroup>
									<Label for="hour_end">
										Hora Final{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="time"
										name="hour_end"
										id="hour_end"
										value={formData.hour_end}
										onChange={handleInputChange}
										invalid={!!formErrors.hour_end}
									/>
									{formErrors.hour_end && (
										<FormFeedback>
											{formErrors.hour_end}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={6}>
								<FormGroup>
									<Label for="place">
										Local{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="place"
										id="place"
										value={formData.place}
										onChange={handleInputChange}
										invalid={!!formErrors.place}
									/>
									{formErrors.place && (
										<FormFeedback>
											{formErrors.place}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>

						{/* Agora a descrição com CKEditor */}
						<Row>
							<Col md={12}>
								<FormGroup>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "10px",
											marginBottom: "10px",
										}}
									>
										<Label
											for="description"
											style={{ margin: 0 }}
										>
											Descrição{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Button
											type="button"
											onClick={handleExportPDF}
											disabled={isLoadingPDF}
											style={{
												background: "#f8f9fa",
												padding: "3px 8px",
												cursor: isLoadingPDF
													? "not-allowed"
													: "pointer",
												color: "black",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: "4px",
												border: "1px solid #dee2e6",
												borderRadius: "3px",
												fontSize: "12px",
												height: "24px",
												opacity: isLoadingPDF ? 0.6 : 1,
											}}
										>
											{isLoadingPDF ? (
												<>
													<span
														className="spinner-border spinner-border-sm"
														style={{
															width: "12px",
															height: "12px",
														}}
													></span>
													Gerando...
												</>
											) : (
												<>
													<FilePdf size={14} /> Gerar
													PDF
												</>
											)}
										</Button>
									</div>
									<CkEditor
										data={formData.description}
										onChange={handleDescriptionChange}
										style={{
											height: "500px",
											width: "100%",
										}}
									/>
									{formErrors.description && (
										<div
											style={{
												color: "#dc3545",
												marginTop: 5,
												fontSize: "0.875em",
											}}
										>
											{formErrors.description}
										</div>
									)}
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md={4}>
								<FormGroup>
									<Label for="notification_type">
										Tipo de Notificação
									</Label>
									<Input
										type="select"
										name="notification_type"
										id="notification_type"
										value={formData.notification_type}
										onChange={handleInputChange}
									>
										<option value="">Nenhum</option>
										<option value="email">Email</option>
									</Input>
								</FormGroup>
							</Col>
							<Col md={4}>
								<FormGroup>
									<Label for="notification_time">
										Tempo de Notificação
									</Label>
									<Input
										type="number"
										name="notification_time"
										id="notification_time"
										value={formData.notification_time}
										onChange={handleInputChange}
										min="0"
									/>
								</FormGroup>
							</Col>
							<Col md={4}>
								<FormGroup>
									<Label for="notification_period">
										Período
									</Label>
									<Input
										type="select"
										name="notification_period"
										id="notification_period"
										value={formData.notification_period}
										onChange={handleInputChange}
									>
										<option value="">Nenhum</option>
										<option value="minutes">Minutos</option>
										<option value="hours">Horas</option>
									</Input>
								</FormGroup>
							</Col>
						</Row>

						<Row>
							<Col md={12}>
								<FormGroup>
									<Label check>
										<Checkbox
											id="recurrence"
											name="recurrence"
											key={checkboxKey}
											defaultChecked={formData.recurrence}
											onChange={(event) => {
												const value = event.target
													.checked
													? true
													: false;

												if (!value) {
													// Se estiver desmarcando, mostra o aviso
													SendaCustomSwal.fire({
														title: "Atenção!",
														html: `
															<p>Ao desmarcar a recorrência, todos os eventos futuros desta série serão excluídos.</p>
															<p>Deseja continuar?</p>
														`,
														icon: "warning",
														showCancelButton: true,
														confirmButtonText:
															"Sim, continuar",
														cancelButtonText:
															"Cancelar",
														customClass: {
															confirmButton:
																"custom-confirm-button",
															cancelButton:
																"custom-cancel-button",
														},
													}).then((result) => {
														if (
															result.isConfirmed
														) {
															setFormData(
																(prev) => ({
																	...prev,
																	recurrence: false,
																	recurrence_freq:
																		"",
																	recurrence_end_date:
																		null,
																	recurrence_group_id:
																		"",
																}),
															);
															setCheckboxKey(
																(prev) =>
																	prev + 1,
															);
														} else {
															setCheckboxKey(
																(prev) =>
																	prev + 1,
															);
														}
													});
												} else {
													// Se estiver marcando, apenas atualiza o estado
													setFormData((prev) => ({
														...prev,
														recurrence: true,
													}));
												}
											}}
											style={{
												background: "none",
												border: "none",
												padding: 0,
												cursor: "pointer",
												color: "black",
												marginRight: 10,
											}}
											icon={<Square size={16} />}
											checkedIcon={
												<CheckSquare size={16} />
											}
										/>
										Recorrência
									</Label>
								</FormGroup>
							</Col>
						</Row>

						{formData.recurrence && (
							<Row>
								<Col md={6}>
									<FormGroup>
										<Label>Tipo de Recorrência</Label>
										<Select
											styles={customStyles}
											placeholder="Selecione o tipo de recorrência"
											menuPosition="fixed"
											menuPlacement="auto"
											menuShouldScrollIntoView={true}
											onChange={(selectedOption) => {
												setFormData((prev) => ({
													...prev,
													recurrence_freq:
														selectedOption
															? selectedOption.value
															: "",
												}));
											}}
											options={[
												{
													value: "daily",
													label: "Diário",
												},
												{
													value: "weekly",
													label: "Semanal",
												},
												{
													value: "monthly",
													label: "Mensal",
												},
												{
													value: "yearly",
													label: "Anual",
												},
											]}
											value={
												formData.recurrence_freq
													? {
															value: formData.recurrence_freq,
															label: {
																daily: "Diário",
																weekly: "Semanal",
																monthly:
																	"Mensal",
																yearly: "Anual",
															}[
																formData
																	.recurrence_freq
															],
													  }
													: null
											}
										/>
									</FormGroup>
								</Col>
								<Col md={6}>
									<FormGroup>
										<Label for="recurrence_end_date">
											Data Final da Recorrência
										</Label>
										<Input
											type="date"
											name="recurrence_end_date"
											id="recurrence_end_date"
											value={formData.recurrence_end_date}
											onChange={handleInputChange}
											min={formData.date_start}
										/>
									</FormGroup>
								</Col>
							</Row>
						)}

						{/* Participantes (não obrigatórios) */}
						<Row>
							<Col md={12}>
								<h5>Participantes</h5>
								<FormGroup>
									<Label>Usuários</Label>
									<CreatableSelect
										isMulti={true}
										styles={customStyles}
										placeholder="Selecione os participantes ou digite um email"
										menuPosition="fixed"
										menuPlacement="auto"
										menuShouldScrollIntoView={true}
										onChange={(selectedOptions) => {
											const selectedParticipants =
												selectedOptions
													? selectedOptions.map(
															(option) => {
																if (
																	option.__isNew__
																) {
																	return {
																		email: option.value,
																		name: option.value,
																	};
																}
																return {
																	user_id:
																		option.value,
																	name: option.label,
																};
															},
													  )
													: [];
											setFormData((prev) => ({
												...prev,
												participants:
													selectedParticipants,
											}));
										}}
										options={companyUsers?.map((user) => ({
											value: user.id,
											label: user.name,
										}))}
										value={formData.participants?.map(
											(participant) => ({
												value:
													participant.user_id ||
													participant.email,
												label: participant.user_id
													? participant.name
													: participant.email,
												__isNew__: !participant.user_id,
											}),
										)}
										isValidNewOption={(inputValue) => {
											// Validação de email
											const emailRegex =
												/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
											return emailRegex.test(inputValue);
										}}
										formatCreateLabel={(inputValue) =>
											`Adicionar "${inputValue}"`
										}
										createOptionPosition="first"
									/>
								</FormGroup>
							</Col>
						</Row>

						<Row className="mt-3">
							<Col
								style={{
									display: "flex",
									justifyContent: "space-between",
								}}
							>
								<div style={{ display: "flex", gap: "10px" }}>
									{id && (
										<>
											<Button
												style={{
													background: "none",
													padding: 10,
													cursor: "pointer",
													color: "black",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: 2,
													width: 150,
													marginTop: 5,
												}}
												type="button"
												onClick={() => {
													setDataLoading(true);
													// Loading para feedback visual da duplicação (1s)
													setTimeout(() => {
														// Remove o id e outros campos que não devem ser duplicados
														const {
															id,
															...meetingData
														} = formData;

														// Navega para a página de criação com os dados do meeting atual
														navigate(
															"/agenda/form",
															{
																state: {
																	duplicatedMeeting:
																		meetingData,
																},
															},
														);
													}, 1000);
												}}
											>
												<Copy size={16} /> Duplicar
											</Button>
											<Button
												style={{
													background: "none",
													padding: 10,
													cursor: "pointer",
													color: "black",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: 2,
													width: 150,
													marginTop: 5,
												}}
												type="button"
												onClick={async () => {
													const result =
														await SendaCustomSwal.fire(
															{
																title: "Tem certeza que deseja excluir?",
																text: formData.recurrence
																	? "Este evento faz parte de uma série recorrente. O que você deseja fazer?"
																	: "Esta ação não poderá ser revertida!",
																icon: "warning",
																showCancelButton: true,
																showDenyButton:
																	formData.recurrence,
																confirmButtonText:
																	formData.recurrence
																		? "Excluir apenas este"
																		: "Sim, excluir!",
																denyButtonText:
																	"Excluir este e todos os futuros",
																cancelButtonText:
																	"Cancelar",
																customClass: {
																	confirmButton:
																		formData.recurrence
																			? "custom-single-button"
																			: "custom-confirm-button",
																	denyButton:
																		"custom-future-button",
																	cancelButton:
																		"custom-cancel-button",
																},
															},
														);

													if (
														result.isConfirmed ||
														result.isDenied
													) {
														try {
															setIsLoadingDelete(
																true,
															);
															await meetingService.delete(
																id,
																formData.recurrence
																	? result.isDenied
																		? "future"
																		: "single"
																	: undefined,
															);
															await SendaCustomSwal.fire(
																{
																	title: "Excluído!",
																	text: "O agendamento foi excluído com sucesso.",
																	icon: "success",
																	customClass:
																		{
																			confirmButton:
																				"custom-confirm-button",
																		},
																},
															);
															navigate("/agenda");
														} catch (error) {
															console.error(
																"Erro ao excluir:",
																error,
															);
															await SendaCustomSwal.fire(
																{
																	title: "Erro",
																	text: "Ocorreu um erro ao excluir o agendamento.",
																	icon: "error",
																	customClass:
																		{
																			confirmButton:
																				"custom-confirm-button",
																		},
																},
															);
														} finally {
															setIsLoadingDelete(
																false,
															);
														}
													}
												}}
											>
												{isLoadingDelete ? (
													<span
														className="spinner-border spinner-border-sm mr-1"
														style={{
															marginRight: 10,
														}}
													></span>
												) : null}
												<Trash size={16} /> Excluir
											</Button>
										</>
									)}
								</div>
								<Button
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										marginLeft: 10,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
										width: 150,
										marginTop: 5,
									}}
									type="submit"
								>
									{isLoading ? (
										<span
											className="spinner-border spinner-border-sm mr-1"
											style={{ marginRight: 10 }}
										></span>
									) : null}
									<FloppyDisk size={16} /> Salvar
								</Button>
							</Col>
						</Row>
					</Form>
				</div>
			</div>
		</Loading>
	);
};

export default withTranslation()(FormMeeting);
